import React from 'react';
import { Link, useLocation } from 'react-router-dom';
import { Button, buttonVariants } from '@/components/ui/button';
import { cn } from '@/lib/utils';
import { MenuToggleIcon } from '@/components/ui/menu-toggle-icon';
import { useScroll } from '@/components/ui/use-scroll';
import { createPortal } from 'react-dom';

const links = [
  { label: 'Home', href: '/' },
  { label: 'About', href: '/about' },
  { label: 'Services', href: '/services' },
  { label: 'Case Studies', href: '/case-studies' },
  { label: 'Why iMANE', href: '/why-imane' },
  { label: 'Careers', href: '/careers' },
  { label: 'Contact', href: '/contact' },
];

export function Header() {
  const [open, setOpen] = React.useState(false);
  const scrolled = useScroll(10);
  const location = useLocation();

  React.useEffect(() => {
    setOpen(false);
  }, [location.pathname]);

  React.useEffect(() => {
    if (open) {
      document.body.style.overflow = 'hidden';
    } else {
      document.body.style.overflow = '';
    }
    return () => {
      document.body.style.overflow = '';
    };
  }, [open]);

  return (
    <header
      data-testid="navbar"
      className={cn(
        'sticky top-0 z-50 w-full border-b border-transparent transition-all duration-200',
        {
          'bg-white/95 supports-[backdrop-filter]:bg-white/70 border-brand-border backdrop-blur-lg':
            scrolled,
        },
      )}
    >
      <nav className="mx-auto flex h-16 w-full max-w-7xl items-center justify-between px-4 sm:px-6 lg:px-8">
        <Link
          to="/"
          className="flex items-center gap-2 flex-shrink-0 rounded-md p-1 hover:bg-slate-100/60 transition-colors"
          data-testid="nav-logo"
        >
          <img
            src="/imane-logo.png"
            alt="iMANE - Boutique Supply Chain & Procurement Specialists"
            className="h-9 w-auto"
          />
          <span className="text-slate-400 text-xs font-source-sans hidden md:block whitespace-nowrap leading-tight">(by MANE LABS LLP)</span>
        </Link>

        <div className="hidden lg:flex items-center gap-1">
          {links.map((link) => {
            const isActive = location.pathname === link.href;
            return (
              <Link
                key={link.label}
                to={link.href}
                className={cn(
                  buttonVariants({ variant: 'ghost', size: 'sm' }),
                  'text-[13px] font-medium tracking-tight px-3 hover:bg-slate-100',
                  isActive
                    ? 'text-brand-orange'
                    : 'text-slate-700 hover:text-brand-navy',
                )}
                data-testid={`nav-link-${link.label.toLowerCase().replace(/ /g, '-')}`}
              >
                {link.label}
              </Link>
            );
          })}
        </div>

        <div className="flex items-center gap-2">
          <a
            href="https://wa.me/919637625311"
            target="_blank"
            rel="noopener noreferrer"
            className="btn-orange hidden lg:inline-flex text-sm py-2 px-4"
            data-testid="nav-cta-consultation"
          >
            Talk to us
          </a>

          <Button
            size="icon"
            variant="outline"
            onClick={() => setOpen(!open)}
            className="lg:hidden border-brand-border"
            aria-expanded={open}
            aria-controls="mobile-menu"
            aria-label="Toggle menu"
            data-testid="mobile-menu-toggle"
          >
            <MenuToggleIcon open={open} className="size-5" duration={300} />
          </Button>
        </div>
      </nav>

      <MobileMenu open={open} className="flex flex-col justify-between gap-2">
        <div className="grid gap-y-1">
          {links.map((link) => {
            const isActive = location.pathname === link.href;
            return (
              <Link
                key={link.label}
                to={link.href}
                className={cn(
                  'flex items-center justify-start rounded-md px-4 py-3 text-base font-medium transition-colors',
                  isActive
                    ? 'text-brand-orange bg-brand-orange-light'
                    : 'text-slate-700 hover:bg-slate-100',
                )}
                data-testid={`mobile-nav-${link.label.toLowerCase().replace(/ /g, '-')}`}
              >
                {link.label}
              </Link>
            );
          })}
        </div>
        <div className="flex flex-col gap-2 px-2 pb-4">
          <a
            href="https://wa.me/919637625311"
            target="_blank"
            rel="noopener noreferrer"
            className="btn-orange w-full justify-center text-sm"
            data-testid="mobile-cta-consultation"
          >
            Talk to us
          </a>
        </div>
      </MobileMenu>
    </header>
  );
}

function MobileMenu({ open, children, className, ...props }) {
  if (!open || typeof window === 'undefined') return null;

  return createPortal(
    <div
      id="mobile-menu"
      data-testid="mobile-menu-panel"
      className={cn(
        'bg-white/95 supports-[backdrop-filter]:bg-white/85 backdrop-blur-lg',
        'fixed top-16 right-0 bottom-0 left-0 z-40 flex flex-col overflow-hidden border-t border-brand-border lg:hidden',
      )}
    >
      <div
        className={cn('size-full p-4', className)}
        {...props}
      >
        {children}
      </div>
    </div>,
    document.body,
  );
}
